import { memo, useCallback, useEffect, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import { useUserLanguage } from '@/customHooks'
import { RentChargeStatusOption, RentChargeTypeOption } from '@/graphql/generated'
import {
  NormalFormSelectionField,
  NormalFormTextField,
} from '../molecules'
import { LoadingSpinner } from '../atoms'
import { rentChargeStatusMapper, rentChargeTypeMapper } from '@/helpers'

type UpdateRentChargeFormControlledProps = {
  defaultData: {
    rentChargeTypeOption: RentChargeTypeOption
    description: string | null
    amountValue: string
    rentChargeStatusOption: RentChargeStatusOption
    currencyOption: string
  } | null
  onChange: (statusOption: RentChargeStatusOption) => void
}

export const UpdateRentChargeFormControlled = memo(({ defaultData, onChange }: UpdateRentChargeFormControlledProps) => {
  const userLanguage = useUserLanguage()

  const [initialData, setInitialData] = useState(defaultData)
  const [currentStatus, setCurrentStatus] = useState<RentChargeStatusOption | null>(defaultData?.rentChargeStatusOption || null)

  useEffect(() => {
    if (!defaultData) return
    setInitialData(defaultData)
    setCurrentStatus(defaultData.rentChargeStatusOption)
  }, [defaultData])

  const onStatusChangeHandle = useCallback((value: RentChargeStatusOption | null) => {
    if (!value) return
    setCurrentStatus(value)
    onChange(value)
  }, [onChange])

  const statusOptions = Object.values(RentChargeStatusOption).map(value => ({
    name: rentChargeStatusMapper(value, userLanguage),
    value: value,
  }))

  const formatAmount = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount)) return amount
    return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(numAmount)
  }

  if (!initialData) {
    return (
      <Row justify='center'>
        <Col xs='content'>
          <LoadingSpinner text />
        </Col>
      </Row>
    )
  }

  return (
    <>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            name="rentChargeType"
            label={userLanguage === 'es' ? 'Tipo de Cargo' : 'Rent Charge Type'}
            value={rentChargeTypeMapper(initialData.rentChargeTypeOption, userLanguage)}
            readOnly
          />
        </Col>
        <Col md={6}>
          <NormalFormSelectionField<RentChargeStatusOption>
            options={statusOptions}
            name="status"
            value={currentStatus}
            label={userLanguage === 'es' ? 'Estado' : 'Status'}
            onChange={(value) => onStatusChangeHandle(value)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <NormalFormTextField
            name="description"
            label={userLanguage === 'es' ? 'Descripción' : 'Description'}
            value={initialData.description || ''}
            readOnly
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            name="amount"
            label={userLanguage === 'es' ? 'Monto' : 'Amount'}
            value={formatAmount(initialData.amountValue, initialData.currencyOption)}
            readOnly
          />
        </Col>
      </Row>
    </>
  )
})

